import { normalizeDomainInput } from '@/lib/domainNormalization';

/**
 * CENTRAL point for client-side organization ↔ supplier-profile relationship resolution.
 *
 * This module is the SINGLE place in the frontend that reads the legacy
 * `organizations.settings.vendor_profile_id` workspace binding, and the single source of
 * truth for resolving the canonical supplier organisation for a vendor profile, deciding a
 * portal switch target, and classifying supplier-workspace access for UI guards.
 *
 * It MIRRORS the backend resolver `public.resolve_vendor_canonical_org`
 * (migrations 20260503210000_ent-1082 + 20260614170200_ent-1814) so the UI preflight and the
 * backend boundary never drift. Backend precedence (typed-column first after ENT-1814):
 *   1. the typed `vendor_profiles.claimant_organization_id` FK                (modelled here)
 *   2. the legacy `organizations.settings.vendor_profile_id` JSON link        (modelled here)
 *   3. the unambiguous `vendor_team_members ∩ organization_members` set       (backend-only;
 *      the client cannot see other orgs' membership, so it never *grants* on this tier —
 *      it only ever resolves within the current user's own member orgs)
 *   4. otherwise NULL  → fail closed
 *
 * Drift is enforced two ways:
 *   - `scripts/check-canonical-org-relationships.mjs` (wired into `pnpm run lint`) forbids
 *     reading the legacy settings binding anywhere except this module. Intentional
 *     exceptions must carry an inline `@canonical-org-exception` marker.
 *   - `src/lib/orgRelationships.contract.test.ts` pins this precedence to the backend rules.
 *
 * Backend authorization remains the source of truth: supplier-only Edge Functions still call
 * `resolve_vendor_canonical_org` and fail closed. Nothing here grants access the user does
 * not already have — switch targets are restricted to the user's own member organisations.
 */

export type OrganizationLike = {
  id: string;
  settings?: unknown;
  slug?: string | null;
};

export type VendorProfileRef =
  | { id?: string | null; claimant_organization_id?: string | null }
  | null
  | undefined;

export type ClaimantOrganizationBinding = {
  claimant_organization_id?: string | null;
};

export const CLAIMANT_ORGANIZATION_ID_COLUMN = 'claimant_organization_id';

/**
 * The ONLY reader of legacy `organizations.settings.vendor_profile_id`.
 * Returns the trimmed binding id, or null when absent/blank/ill-typed.
 */
export function readWorkspaceVendorProfileId(
  org: { settings?: unknown } | null | undefined,
): string | null {
  const settings = org?.settings;
  if (!settings || typeof settings !== 'object') {
    return null;
  }
  const value = (settings as Record<string, unknown>).vendor_profile_id;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Test-seed compatibility helper for legacy E2E fixtures that still need to write
 * `organizations.settings.vendor_profile_id` while the runtime migrates to the typed
 * `vendor_profiles.claimant_organization_id` FK. Production claim paths must use the
 * backend RPCs instead of writing this JSON link.
 */
export function withWorkspaceVendorProfileId<T extends Record<string, unknown>>(
  settings: T,
  vendorProfileId: string | null | undefined,
): T & { vendor_profile_id?: string } {
  const trimmed = typeof vendorProfileId === 'string' ? vendorProfileId.trim() : '';
  if (!trimmed) {
    const { vendor_profile_id: _legacyVendorProfileId, ...rest } = settings;
    return rest as T & { vendor_profile_id?: string };
  }
  return { ...settings, vendor_profile_id: trimmed };
}

/**
 * The ONLY reader of the typed `vendor_profiles.claimant_organization_id` foreign key — the
 * post-ENT-1814 binding that supersedes the legacy JSON `settings.vendor_profile_id`. Returns
 * the trimmed organisation id, or null when absent/blank/ill-typed.
 */
export function readClaimantOrganizationId(
  vendorProfile: { claimant_organization_id?: unknown } | null | undefined,
): string | null {
  const value = vendorProfile?.claimant_organization_id;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * The ONLY reader of `organizations.settings.domain` (ENT-3472).
 * Returns the normalized domain (via normalizeDomainInput), or null when
 * absent/blank/ill-typed. Never read `settings.domain` raw elsewhere.
 */
export function readWorkspaceDomain(
  org: { settings?: unknown } | null | undefined,
): string | null {
  const settings = org?.settings;
  if (!settings || typeof settings !== 'object') {
    return null;
  }
  const value = (settings as Record<string, unknown>).domain;
  return typeof value === 'string' ? normalizeDomainInput(value) : null;
}

/**
 * The ONLY writer of the typed `vendor_profiles.claimant_organization_id` binding on a vendor
 * profile row. Returns a shallow copy with the column set (trimmed) or null when the
 * organisation id is blank. The authoritative write happens server-side in
 * `set_vendor_canonical_org`; this mirrors it for optimistic client state.
 */
export function withClaimantOrganizationId<T extends Record<string, unknown>>(
  vendorProfile: T,
  organizationId: string | null | undefined,
): T & { claimant_organization_id: string | null } {
  const trimmed = typeof organizationId === 'string' ? organizationId.trim() : '';
  return { ...vendorProfile, claimant_organization_id: trimmed ? trimmed : null };
}

/** Dedupe a list of organisations by id, preserving order and dropping nullish entries. */
export function dedupeOrganizations<T extends { id: string }>(
  orgs: ReadonlyArray<T | null | undefined>,
): T[] {
  const out: T[] = [];
  const seen = new Set<string>();
  for (const org of orgs) {
    if (!org || seen.has(org.id)) {
      continue;
    }
    seen.add(org.id);
    out.push(org);
  }
  return out;
}

/**
 * Forward resolver: the canonical supplier organisation for `vendorProfile`. Mirrors the
 * backend precedence:
 * - typed `claimant_organization_id` set on the profile → that org id (authoritative tier 1)
 * - else exactly one member org whose JSON binding === profile.id → that org id (tier 2)
 * - else no member org binds the profile           → fall back to the active org id
 * - else more than one member org binds the profile → null (ambiguous; fail closed)
 * - no profile                                     → the active org id
 */
export function resolveCanonicalSupplierOrganizationId(params: {
  organization: OrganizationLike | null;
  organizations?: OrganizationLike[];
  vendorProfile: VendorProfileRef;
}): string | null {
  const { organization, organizations, vendorProfile } = params;
  const list = organizations ?? [];
  if (!vendorProfile?.id) {
    return organization?.id ?? null;
  }
  // Tier 1: the typed claimant_organization_id FK on the profile (mirrors the backend
  // resolver's typed-first precedence). When present it is authoritative; the switch gate
  // (resolveSupplierPortalSwitch) independently requires membership, so a non-member typed
  // org still fails closed there rather than granting a switch.
  const typedClaimantOrgId = readClaimantOrganizationId(vendorProfile);
  if (typedClaimantOrgId) {
    return typedClaimantOrgId;
  }
  // Tier 2: the legacy JSON binding among the user's own member orgs.
  const matches = list.filter((candidate) => readWorkspaceVendorProfileId(candidate) === vendorProfile.id);
  if (matches.length === 1) {
    return matches[0].id;
  }
  if (matches.length === 0) {
    return organization?.id ?? null;
  }
  return null;
}

/**
 * Switch decision for entering the supplier portal. Switch only to a REAL, distinct member
 * org — never to the active org (no-op) and never to an org the user is not a member of
 * (which would be a non-fail-closed grant). Non-members are left on the active org so the
 * backend boundary and the shipped page guard fail-close.
 */
export function resolveSupplierPortalSwitch(params: {
  organization: OrganizationLike | null;
  memberOrganizations: OrganizationLike[];
  vendorProfile: VendorProfileRef;
}): { shouldSwitch: boolean; targetOrgId: string | null } {
  const { organization, memberOrganizations, vendorProfile } = params;
  const targetOrgId = resolveCanonicalSupplierOrganizationId({
    organization,
    organizations: memberOrganizations,
    vendorProfile,
  });
  const shouldSwitch = Boolean(
    targetOrgId
      && targetOrgId !== organization?.id
      && memberOrganizations.some((candidate) => candidate.id === targetOrgId),
  );
  return { shouldSwitch, targetOrgId };
}

/**
 * Guard classification for supplier-only surfaces. Computes the two recoverable states the
 * shipped guard renders from the SAME binding source the switch/resolver use, so the UI
 * preflight and the backend boundary agree:
 * - `wrongActiveWorkspace`: the active workspace is bound to a different supplier profile, or
 *   (when it has no explicit binding) its domain does not match the supplier profile domain.
 * - `missingSupplierProfileAccess`: the active workspace matches, but the user has no accepted
 *   supplier-profile membership (userRole === null).
 */
export type SupplierWorkspaceAccess = {
  wrongActiveWorkspace: boolean;
  missingSupplierProfileAccess: boolean;
};

export function classifySupplierWorkspaceAccess(params: {
  organization: { id?: string | null; settings?: unknown } | null | undefined;
  vendorProfile: { id?: string | null; domain?: string | null; claimant_organization_id?: string | null } | null | undefined;
  userRole: string | null | undefined;
}): SupplierWorkspaceAccess {
  const { organization, vendorProfile, userRole } = params;
  const vendorProfileId = vendorProfile?.id ?? null;
  const typedClaimantOrgId = readClaimantOrganizationId(vendorProfile);
  const workspaceVendorProfileId = readWorkspaceVendorProfileId(organization);
  const workspaceDomain = readWorkspaceDomain(organization);
  const vendorProfileDomain = normalizeDomainInput(vendorProfile?.domain);
  const hasTypedClaimantMismatch = Boolean(
    typedClaimantOrgId
      && organization?.id
      && typedClaimantOrgId !== organization.id,
  );

  const hasWorkspaceDomainMismatch = Boolean(
    !typedClaimantOrgId
      && !workspaceVendorProfileId
      && workspaceDomain
      && vendorProfileDomain
      && workspaceDomain !== vendorProfileDomain,
  );
  const wrongActiveWorkspace = Boolean(
    vendorProfileId
      && (
        hasTypedClaimantMismatch
        || (!typedClaimantOrgId && workspaceVendorProfileId && workspaceVendorProfileId !== vendorProfileId)
        || hasWorkspaceDomainMismatch
      ),
  );
  const missingSupplierProfileAccess = Boolean(vendorProfileId && (userRole ?? null) === null);

  return { wrongActiveWorkspace, missingSupplierProfileAccess };
}
